/**
 * LocationPicker — autocomplete + free-form address with Nominatim geocoding.
 *
 * Used by the Exception form to capture WHERE the override applies:
 *   • Type `poi`     — selected from /api/poi/search (transit_stops + planned_services.stops)
 *   • Type `address` — free-form, optionally geocoded via /api/geocode (Nominatim, cached, 1 req/s)
 *   • Type `none`    — no specific location, the exception only changes who drives
 *
 * Emits a single ``value`` object the parent can serialise straight into
 * a tour_assignments PATCH body:
 *   { location_type, poi_id, poi_name, address, latitude, longitude }
 */
import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Search, X, Loader2, CheckCircle2 } from "lucide-react";

const EMPTY_VALUE = {
  location_type: "none",
  poi_id: null,
  poi_name: null,
  address: null,
  latitude: null,
  longitude: null,
};

export default function LocationPicker({ value, onChange, testIdPrefix = "loc" }) {
  const v = value || EMPTY_VALUE;
  const [mode, setMode] = useState(v.location_type === "address" ? "address" : "poi");
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounced autocomplete (only in poi mode)
  useEffect(() => {
    if (mode !== "poi" || !query || query.length < 2) {
      setSuggestions([]); return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get(`/poi/search?q=${encodeURIComponent(query)}&limit=12`);
        setSuggestions(r.data?.items || []);
        setOpen(true);
      } catch (_e) {
        setSuggestions([]);
      } finally { setSearching(false); }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, mode]);

  const pickSuggestion = async (item) => {
    // Initial fill from the suggestion itself
    const base = {
      location_type: "poi",
      poi_id: item.id || null,
      poi_name: item.name,
      address: item.city || null,
      latitude: item.latitude ?? null,
      longitude: item.longitude ?? null,
    };
    onChange(base);
    setQuery("");
    setOpen(false);
    // Auto-enrich with the full street address from Nominatim
    setGeocoding(true);
    try {
      let enriched = null;
      if (item.latitude != null && item.longitude != null) {
        // Reverse-geocode the known coordinates → full street address
        const r = await api.post("/geocode/reverse", { lat: item.latitude, lon: item.longitude });
        const res = r.data?.result;
        if (res?.display_name) {
          enriched = { ...base, address: res.display_name };
        }
      } else {
        // Forward-geocode the POI name → coords + display_name
        const r = await api.post("/geocode", { address: item.city ? `${item.name}, ${item.city}` : item.name });
        const res = r.data?.result;
        if (res?.lat != null) {
          enriched = {
            ...base,
            address: res.display_name || base.address,
            latitude: res.lat,
            longitude: res.lon,
          };
        }
      }
      if (enriched) onChange(enriched);
    } catch (_e) {
      // Non-blocking: keep the base selection even if geocoding fails
    } finally { setGeocoding(false); }
  };

  const geocodeFree = async () => {
    const addr = (v.address || "").trim();
    if (addr.length < 3) {
      toast.error("Adresse trop courte"); return;
    }
    setGeocoding(true);
    try {
      const r = await api.post("/geocode", { address: addr });
      onChange({
        ...v,
        location_type: "address",
        latitude: r.data?.result?.lat ?? null,
        longitude: r.data?.result?.lon ?? null,
        address: r.data?.result?.display_name || addr,
      });
      toast.success("Adresse géolocalisée");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Géocodage impossible");
    } finally { setGeocoding(false); }
  };

  const clear = () => {
    onChange({ ...EMPTY_VALUE });
    setQuery("");
    setSuggestions([]);
  };

  const hasValue = v.poi_name || v.address;

  return (
    <div ref={wrapRef} className="space-y-2" data-testid={`${testIdPrefix}-picker`}>
      {/* Mode tabs */}
      <div className="flex gap-1">
        {[
          { k: "poi", l: "Arrêt / POI" },
          { k: "address", l: "Adresse libre" },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setMode(t.k)}
            data-testid={`${testIdPrefix}-mode-${t.k}`}
            className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest border ${
              mode === t.k ? "bg-zinc-900 text-[#FFC60A] border-zinc-900" : "bg-white border-zinc-300 hover:border-zinc-900"
            }`}
          >
            {t.l}
          </button>
        ))}
        {hasValue && (
          <button
            type="button" onClick={clear}
            data-testid={`${testIdPrefix}-clear`}
            className="ml-auto px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-red-600 inline-flex items-center gap-1"
          >
            <X size={11} /> Effacer
          </button>
        )}
      </div>

      {/* POI search */}
      {mode === "poi" && (
        <div className="relative">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => suggestions.length && setOpen(true)}
              placeholder="Rechercher un arrêt ou POI…"
              data-testid={`${testIdPrefix}-search-input`}
              className="w-full border border-zinc-900 pl-8 pr-3 py-2 text-sm"
            />
            {searching && <Loader2 size={14} className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-zinc-400" />}
          </div>
          {open && suggestions.length > 0 && (
            <div
              className="absolute z-20 left-0 right-0 mt-1 bg-white border border-zinc-900 shadow-[4px_4px_0_0_#000] max-h-64 overflow-auto"
              data-testid={`${testIdPrefix}-suggestions`}
            >
              {suggestions.map((s, i) => (
                <button
                  key={`${s.source}-${s.id || s.name}-${i}`}
                  type="button"
                  onClick={() => pickSuggestion(s)}
                  data-testid={`${testIdPrefix}-suggestion-${i}`}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 border-b border-zinc-100 last:border-b-0 flex items-start gap-2"
                >
                  <MapPin size={12} className={`mt-0.5 ${s.has_geo ? "text-emerald-600" : "text-zinc-300"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{s.name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">
                      {s.source === "stop" ? "ARRÊT GTFS" : "ARRÊT IMPORTÉ"}
                      {s.city ? ` · ${s.city}` : ""}
                      {!s.has_geo ? " · sans GPS" : ""}
                      {s.usage_count ? ` · ${s.usage_count}×` : ""}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Free-form address */}
      {mode === "address" && (
        <div className="flex gap-2">
          <input
            value={v.address || ""}
            onChange={(e) => onChange({ ...v, location_type: "address", address: e.target.value, poi_id: null, poi_name: null })}
            placeholder="Ex: Route de Lausanne 12, 1145 Bière"
            data-testid={`${testIdPrefix}-address-input`}
            className="flex-1 border border-zinc-900 px-3 py-2 text-sm"
          />
          <button
            type="button" onClick={geocodeFree} disabled={geocoding}
            data-testid={`${testIdPrefix}-geocode`}
            className="px-3 py-2 bg-zinc-900 text-[#FFC60A] text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 hover:bg-zinc-800 disabled:opacity-50"
          >
            {geocoding ? <Loader2 size={12} className="animate-spin" /> : <MapPin size={12} />}
            Géocoder
          </button>
        </div>
      )}

      {/* Current selection preview */}
      {hasValue && (
        <div
          className="bg-emerald-50 border border-emerald-300 px-3 py-2 text-xs flex items-start gap-2"
          data-testid={`${testIdPrefix}-preview`}
        >
          <CheckCircle2 size={12} className="text-emerald-700 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-bold truncate flex items-center gap-1.5">
              {v.poi_name || v.address}
              {geocoding && mode === "poi" && (
                <Loader2 size={10} className="animate-spin text-emerald-700" data-testid={`${testIdPrefix}-enriching`} />
              )}
            </div>
            {v.poi_name && v.address && <div className="text-zinc-600 text-[11px]">{v.address}</div>}
            {(v.latitude != null && v.longitude != null) && (
              <div className="text-[10px] font-mono text-zinc-500">
                {Number(v.latitude).toFixed(5)}, {Number(v.longitude).toFixed(5)}
              </div>
            )}
            {v.latitude == null && v.location_type === "poi" && !geocoding && (
              <div className="text-[10px] text-orange-700 font-mono">⚠ Sans coordonnées GPS</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
